import { useCallback, useMemo } from 'react';

import {
  LoadingOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { BackTop, Badge, Empty, Input, Space, Spin, Typography } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchRolesWithParams,
  hydrateFromUrl,
  searchRolesByNameWithParams,
  setRoleSearchQuery,
} from '../../../store/features/acl/roles/rolesSlice';
import { ListApiConfig } from '../../../types/list';
import { UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import { DrawerHashRoute } from '../../containers/Drawers/types';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import NoResultsFound from '../../elements/NoResultsFound';
import ViewWrapper from '../../elements/ViewWrapper';
import RoleTableList from './components/RoleTableList/RoleTableList';
import './RolesView.less';

const { Title } = Typography;

const antSpinIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

const RolesView = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { hasPermission } = useAuth();
  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'updated_at:desc',
      columns: 'permissions.*',
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
    }),
    []
  );

  const {
    value: roles,
    fetchingRoles,
    searchingRoles,
    totalCount,
    page,
    pageSize,
    search: storeSearch,
    sort,
  } = useAppSelector((state) => state.roles);

  const noSearchResultsFound = useMemo(
    () => !fetchingRoles && !!storeSearch && (roles?.length || 0) === 0,
    [fetchingRoles, storeSearch, roles?.length]
  );

  const createNewRoleButton = useMemo(
    (): JSX.Element => (
      <Link to={DrawerHashRoute.RoleForm}>
        <Button type="primary" data-cy="create-new-role-btn">
          Create New Role
          <PlusOutlined />
        </Button>
      </Link>
    ),
    []
  );

  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    onHydrate: (urlControls) => dispatch(hydrateFromUrl(urlControls)),
  });

  const fetchRolesThunk = useCallback(
    (params: Record<string, any>) =>
      dispatch(fetchRolesWithParams({ params })) as any,
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: { page, pageSize, search: storeSearch, sort },
    config: listApiConfig,
    fetchThunk: fetchRolesThunk,
  });

  const onSearch = useCallback(
    (value: string) => {
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort },
        listApiConfig
      );
      return dispatch(searchRolesByNameWithParams({ params }));
    },
    [dispatch, pageSize, sort, listApiConfig]
  );

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: storeSearch,
    setStoreSearch: (value) => dispatch(setRoleSearchQuery(value)),
    pageSize,
    sort,
    history,
    location,
    onSearch,
  });

  const headerSectionRight = useMemo(
    (): JSX.Element => (
      <Space direction="horizontal" size="middle">
        <Input
          allowClear={!searchingRoles}
          value={searchValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search for a role..."
          prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
          suffix={searchingRoles && <Spin indicator={antSpinIcon} />}
        />
        {hasPermission(UserPermissions.RolesCreate) && createNewRoleButton}
      </Space>
    ),
    [createNewRoleButton, searchValue, searchingRoles, hasPermission, onChange]
  );

  // Effects replaced by shared hooks

  if (!hasPermission(UserPermissions.RolesView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Roles</Title>
          <Badge
            size="default"
            count={totalCount as number}
            className="total-count-badge"
          />
        </Space>
      }
      headerSectionRight={headerSectionRight}
    >
      {fetchingRoles ? (
        <RoleTableList roles={[]} loading={true} view="Roles" />
      ) : noSearchResultsFound ? (
        <NoResultsFound searchQuery={storeSearch} onClear={() => clear()} />
      ) : (roles?.length || 0) === 0 ? (
        <Empty
          description="Currently, there are no roles"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {hasPermission(UserPermissions.RolesCreate) && createNewRoleButton}
        </Empty>
      ) : (
        <RoleTableList roles={roles} loading={fetchingRoles} view="Roles" />
      )}

      <BackTop />
    </ViewWrapper>
  );
};

export default RolesView;
